import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { OneTapSignIn } from "../components/OneTapSignIn";

function LoginPage() {
  console.log("🔥 LoginPage COMPONENT MOUNTED - LOG:");
  const [step, setStep] = useState<"email" | "verify">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [testOtp, setTestOtp] = useState<string | null>(null);
  const navigate = useNavigate();
  const { startOtpFlow, verifyOtp, isAuthenticated } = useAuth();

  console.log("🔥 LoginPage: useAuth values:", {
    startOtpFlow,
    verifyOtp,
    isAuthenticated,
  });

  // Redirect if already authenticated
  useEffect(() => {
    console.log(
      "🔍 LoginPage: Checking auth state, isAuthenticated:",
      isAuthenticated
    );
    if (isAuthenticated) {
      console.log("✅ LoginPage: User authenticated, redirecting to dashboard");
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const startLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setError("");

    const result = await startOtpFlow(email);

    if (result.success) {
      setOtp(""); // Reset OTP field
      setStep("verify");
    } else {
      setError(result.error || "Failed to send verification code");
    }
  };

  const completeLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otp) {
      setError("Please enter the verification code");
      return;
    }

    if (otp.length !== 6) {
      setError("Please enter a valid 6-digit verification code");
      return;
    }

    setError("");

    const result = await verifyOtp(email, otp);

    if (result.success) {
      // useAuth hook will automatically update the auth state and redirect
      // The useEffect above will handle the redirect when isAuthenticated becomes true
    } else {
      setError(result.error || "Verification failed. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold mx-auto mb-4">
            SG
          </div>
          <h2 className="text-2xl font-bold">Welcome Back</h2>
          <p className="text-gray-600">
            Sign in to your ShopGlobal Pro account
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
            {error}
          </div>
        )}

        {step === "email" ? (
          <form onSubmit={startLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="john@example.com"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {isLoading ? "Sending Code..." : "Send Verification Code"}
            </button>
          </form>
        ) : (
          <form onSubmit={completeLogin} className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="text-green-800 text-sm">
                ✅ Verification code sent to <strong>{email}</strong>
              </div>
              <div className="text-green-600 text-xs mt-1">
                Check your email (including spam folder) and enter the 6-digit
                code.
              </div>
              {testOtp && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mt-3">
                  <div className="text-yellow-800 text-xs font-mono text-center">
                    🔧 TEST CODE: <span className="font-bold">{testOtp}</span>
                    <br />
                    <span className="text-yellow-600">
                      (Remove this in production)
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Verification Code
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl font-mono tracking-wider"
                placeholder="000000"
                maxLength={6}
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {isLoading ? "Signing In..." : "Sign In"}
            </button>

            <button
              type="button"
              onClick={() => setStep("email")}
              className="w-full text-blue-600 hover:text-blue-700 text-sm font-medium">
              ← Change Email
            </button>
          </form>
        )}

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">
              Or continue with
            </span>
          </div>
        </div>

        {/* Google Sign In Button */}
        <GoogleSignInButton
          onSuccess={() => {
            console.log("✅ Google Sign In successful, redirecting...");
            navigate("/dashboard", { replace: true });
          }}
          onError={(error) => {
            console.error("❌ Google Sign In error:", error);
            setError(error);
          }}
          className="w-full"
        />

        {/* One-Tap Sign In Button */}
        <div className="mt-4">
          <OneTapSignIn
            onSuccess={() => {
              console.log("✅ One-Tap Sign In successful, redirecting...");
              navigate("/dashboard", { replace: true });
            }}
            onError={(error) => {
              console.error("❌ One-Tap Sign In error:", error);
              setError(error);
            }}
            className="w-full"
          />
        </div>

        <p className="text-center text-sm text-gray-600 mt-6">
          Don't have an account?{" "}
          <Link to="/signup" className="text-blue-600 hover:text-blue-700">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
